/**
 * Collapsible day group in the list view.
 *
 * Shows a header with label, task count, and collapse toggle.
 * Renders ListViewRow for each task, or ListViewTreeNode when a task
 * has children visible in the current tasks set.
 */

import { useState } from "react";
import { Button } from "@/components/ui";
import { cn } from "@/lib/cn";
import { Icons } from "@/components/ui/icons";
import type { Task } from "@/api/tasks";
import { ListViewRow } from "./list-view-row";
import { ListViewTreeNode } from "./list-view-tree-node";

interface ListViewGroupProps {
  /** Stable group id for test ids; `label` is the translated header. */
  id: string;
  label: string;
  labelColor?: string;
  tasks: Task[];
  defaultCollapsed?: boolean;
  onSelectTask: (taskId: string) => void;
  /**
   * Map of parent_task_id → list of visible children. Parents listed here
   * render with a tree toggle and, when expanded, their children inline.
   * Children still in this group should have been excluded upstream so
   * they aren't rendered twice.
   */
  childrenByParent?: Map<string, Task[]>;
}

export function ListViewGroup({
  id,
  label,
  labelColor,
  tasks,
  defaultCollapsed = false,
  onSelectTask,
  childrenByParent,
}: ListViewGroupProps) {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);

  if (tasks.length === 0) return null;

  return (
    <div data-testid={`list-view-group-${id}`}>
      {/* Group header */}
      <Button
        variant="ghost"
        type="button"
        onClick={() => setCollapsed(!collapsed)}
        className="flex h-auto w-full min-w-0 justify-start gap-2 px-2 py-1.5 text-left text-appkit-caption text-muted"
        aria-expanded={!collapsed}
        data-testid="list-view-group-header"
      >
        <Icons.ChevronRight
          className={cn(
            "h-3 w-3 text-muted transition-transform",
            !collapsed && "rotate-90",
          )}
        />
        <span
          className="min-w-0 truncate text-appkit-caption font-semibold uppercase tracking-[0.08em]"
          style={{ color: labelColor }}
        >
          {label}
        </span>
        <span className="text-appkit-small text-muted">{tasks.length}</span>
      </Button>

      {/* Task rows */}
      {!collapsed && (
        <div>
          {tasks.map((task) => {
            const children = childrenByParent?.get(task.id);
            if (children && children.length > 0) {
              return (
                <ListViewTreeNode
                  key={task.id}
                  parent={task}
                  children={children}
                  onSelectTask={onSelectTask}
                  childrenByParent={childrenByParent}
                />
              );
            }
            return (
              <ListViewRow key={task.id} task={task} onSelect={onSelectTask} />
            );
          })}
        </div>
      )}
    </div>
  );
}
